import React, { useContext } from "react"
import CharLimit from "../ui/charLimit"
import CloseModal from "../CloseModal"
import DragDropArea from "../DragDropArea"
import { Button } from "../ui/button"
import { CoachPrivateProfileContext } from "../../pages/manage-business/profile/context/coachPrivateProfileContext"
import { CommonFunctionCtx } from "../../context/commonFunctionContext"
import { SvgArrowLeftRight } from "../icons/arrow-left-right"
import { SvgQuoteRight2 } from "../icons"

interface Props {
  handleClick: any
  bentoSelection: number
  updateBento: (
    bentoSpot: number | null,
    selection: string,
    contentId?: number,
    content?: string,
    url?: string
  ) => void
}

export default function BentoQuoteFlow({
  handleClick,
  updateBento,
  bentoSelection,
}: Props) {
  const { setPopupNotification } = React.useContext(CommonFunctionCtx)
  const [quoteImageFile, setQuoteImageFile] = React.useState<File>()
  const [quoteImageProperties, setQuoteImageProperties] = React.useState<any>({
    base64: "",
    type: "",
  })
  const [previewImgUrl, setPreviewImgUrl] = React.useState<string>("")

  const { coachPublicProfileData } = useContext(CoachPrivateProfileContext)
  const [quoteText, setQuoteText] = React.useState<string>(() => {
    if (bentoSelection === 3) {
      return coachPublicProfileData?.bento_box_three?.data?.quote
    } else if (bentoSelection === 4) {
      return coachPublicProfileData?.bento_box_four?.data?.quote
    } else if (bentoSelection === 5) {
      return coachPublicProfileData?.bento_box_five?.data?.quote
    }
  })

  const validateFile = (imageFile: File) => {
    if (
      (imageFile.type === "image/png" ||
        imageFile.type === "image/jpg" ||
        imageFile.type === "image/jpeg") &&
      imageFile.size <= 5000000 &&
      imageFile.size > 0
    ) {
      return true
    } else {
      return false
    }
  }

  const getQuoteImageProperties = () => {
    if (quoteImageFile) {
      if (validateFile(quoteImageFile)) {
        const type = quoteImageFile.type.split("/")[1]
        const reader = new FileReader()
        reader.readAsDataURL(quoteImageFile)
        reader.onload = function () {
          const string = (reader.result as string).split("\n").map((data) => {
            return data.split(",")
          })
          const base64 = string[0][1]
          setQuoteImageProperties({ base64: base64, type: type })
        }
        reader.onerror = function (error) {
          console.log("Error: ", error)
        }
      } else {
        setPopupNotification({
          show: true,
          title: "Bummer! Your file is too big.",
          message:
            "Give it another try with a file under 5 megabytes, and in one of the following formats: png, jpg, or jpeg.",
          callback: null,
        })
        setQuoteImageFile(undefined)
        setQuoteImageProperties({ base64: "", type: "" })
      }
    }
  }

  React.useEffect(() => {
    getQuoteImageProperties()
    if (quoteImageFile) {
      setPreviewImgUrl(URL.createObjectURL(quoteImageFile))
    }
  }, [quoteImageFile])

  return (
    <>
      <div className="fixed top-0 left-0 w-screen h-screen flex items-center justify-center zoee-backdrop-filter z-[1009]">
        <div className="w-[775px] max-w-[90%] main-shadow rounded-[16px] pt-[76px] pb-[40px] mt-[90px] px-[16px] md:px-[55px] max-h-[85vh] bg-white fixed flex flex-col items-center overflow-y-auto">
          <CloseModal
            callback={() => {
              handleClick("cancel")
            }}
            styling="absolute top-[24px] right-[32px] flex items-center justify-center w-[32px] h-[32px] rounded-full bg-grayFlash"
          />
          <h2 className="text-center top-[10px] left-[10px] sm:left-[30px] sm:top-[30px] md:left-[50px] text-[16px] md:text-[28px] lg:text-[16px] sm:mt-0">
            Featured Content
          </h2>
          <div className="flex gap-x-2 justify-center items-center">
            <SvgQuoteRight2 height="40" width="40" />
            <p className="font-bold text-[20px] sm:mt-4 md:-translate-y-2">
              Quote
            </p>
          </div>
          <Button
            variant={"link"}
            onClick={() => {
              handleClick("switch content")
            }}
          >
            <SvgArrowLeftRight />
            Switch content type
          </Button>

          <div className="flex flex-col justify-center items-center mt-4 w-full">
            <div className="flex flex-col gap-[8px] w-full">
              <div className="flex items-center justify-between gap-[8px]">
                <p className="font-bold text-[16px]">Quote image</p>
                <span className="font-normal text-sm text-graySlate">
                  Optional
                </span>
              </div>
              <DragDropArea
                styling={`w-full rounded-[16px] flex-row h-[100px]`}
                mediaFile={quoteImageFile}
                setMediaFile={setQuoteImageFile}
                mediaType="image"
              />
            </div>
            <div className="flex flex-col justify-center items-center rounded-[16px] mt-4">
              {previewImgUrl ? (
                <div className="w-[200px] rounded-[16px] h-[200px]">
                  <img
                    src={previewImgUrl}
                    alt="preview"
                    className="w-full h-full rounded-[16px] object-cover"
                  />
                </div>
              ) : (
                <p className="text-graySlate text-center text-sm min-w-[300px] mb-5">
                  Max file size is 5MB. Recommended 300 x 300 px at 150 DPI.{" "}
                  <br />
                  File types: .png, .jpg, and .jpeg
                </p>
              )}
              <p className="text-blurple text-center">
                Don't have an image for your quote?
              </p>
              <p className="text-blurple text-center">
                Get a free stock photo here{" "}
                <a
                  href="https://pixabay.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://pixabay.com
                </a>
              </p>
            </div>
          </div>
          <div className="flex flex-col justify-center items-center w-full mt-8">
            <span className="w-full flex justify-between">
              <p>
                <b>Quote</b>
              </p>
              <CharLimit inputText={quoteText} charNumber={250} />
            </span>
            <textarea
              name="quote"
              value={quoteText}
              maxLength={250}
              onChange={(e) => setQuoteText(e.target.value)}
              className={`whitespace-pre-line resize-none border-[1px] border-solid text-graySlate text-[16px] bg-white w-full h-[150px] py-[16px] px-[20px] rounded-[10px] focus:outline-0 focus:border focus:border-solid focus:border-black focus:text-black border-grayCloud mb-6`}
              placeholder={quoteText || "Enter your quote here..."}
            ></textarea>
          </div>
          <div className="flex gap-x-2">
            <Button
              size="lg"
              variant="outline"
              onClick={() => {
                handleClick("cancel")
              }}
            >
              Cancel
            </Button>
            <Button
              size="lg"
              variant='default'
              onClick={() => {
                updateBento(
                  bentoSelection,
                  "quote",
                  undefined,
                  quoteText,
                  quoteImageProperties?.base64
                    ? `data:image/${quoteImageProperties.type};base64,${quoteImageProperties.base64}`
                    : undefined
                )
                handleClick("cancel")
              }}
              disabled={!quoteText?.trim()}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
